import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, Music, Download, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductCard = ({ product, onBuy, loading }) => {
  const isAlbum = product.type === 'album';

  return (
    <div className={`bg-gradient-to-br from-gray-900 to-teal-950 border border-teal-900/30 rounded-2xl overflow-hidden hover:border-gold-500/50 transition-all duration-300 ${isAlbum ? 'md:col-span-2 md:flex' : ''}`}>
      <div className={`relative overflow-hidden ${isAlbum ? 'md:w-1/2' : 'aspect-square'}`}>
        <img
          src={product.cover}
          alt={product.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3">
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${isAlbum ? 'bg-gold-500 text-black' : 'bg-teal-600 text-white'}`}>
            {product.type}
          </span>
        </div>
      </div>
      <div className={`p-6 flex flex-col justify-between ${isAlbum ? 'md:w-1/2' : ''}`}>
        <div>
          <h2 className="text-xl font-bold text-teal-50 mb-1">{product.name}</h2>
          <p className="text-teal-300 text-sm mb-4">{product.artist}</p>
          {isAlbum && (
            <p className="text-teal-200/70 text-sm mb-4">
              The debut album featuring soulful originals and genre-defining tracks. Available for digital download.
            </p>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-3xl font-bold text-gold-400">${product.price.toFixed(2)}</span>
          <button
            onClick={() => onBuy(product.id)}
            disabled={loading}
            className="bg-gold-500 hover:bg-gold-600 disabled:bg-gray-600 text-black font-bold px-6 py-3 rounded-full transition-all duration-200 hover:scale-105 flex items-center gap-2"
          >
            {loading ? <Loader size={18} className="animate-spin" /> : <ShoppingCart size={18} />}
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentStatus = ({ status, onBack, downloadFiles, onFormatChange, selectedFormat }) => {
  if (status === 'polling') {
    return (
      <div className="text-center py-16 space-y-4">
        <Loader size={48} className="animate-spin text-gold-400 mx-auto" />
        <h2 className="text-2xl font-bold text-teal-50">Processing Payment...</h2>
        <p className="text-teal-300">Please wait while we confirm your purchase.</p>
      </div>
    );
  }

  if (status === 'paid') {
    return (
      <div className="text-center py-16 space-y-6">
        <CheckCircle size={64} className="text-green-400 mx-auto" />
        <h2 className="text-2xl font-bold text-teal-50">Payment Successful!</h2>
        <p className="text-teal-300">Thank you for your purchase. Choose your format and download below.</p>

        {/* Format Selector */}
        <div className="flex items-center justify-center gap-3">
          <span className="text-teal-300 text-sm font-medium">Format:</span>
          {['wav', 'mp3', 'aac'].map((fmt) => (
            <button
              key={fmt}
              onClick={() => onFormatChange(fmt)}
              className={`px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all duration-200 ${
                selectedFormat === fmt
                  ? 'bg-gold-500 text-black'
                  : 'bg-gray-800 text-teal-300 border border-teal-700 hover:border-gold-500'
              }`}
            >
              {fmt}
            </button>
          ))}
        </div>

        {downloadFiles && downloadFiles.length > 0 && (
          <div className="space-y-2 max-w-lg mx-auto text-left">
            {downloadFiles.map((file) => (
              <a
                key={file.name || file.download_url}
                href={file.url || `${BACKEND_URL}${file.download_url}`}
                download={file.name}
                className="flex items-center gap-3 bg-gray-900/80 hover:bg-gray-800 border border-teal-900/40 hover:border-gold-500/50 px-4 py-3 rounded-lg transition-all duration-200 group"
              >
                <Download size={16} className="text-gold-400 flex-shrink-0" />
                <span className="text-teal-100 text-sm truncate flex-1">{file.name}</span>
                <span className="text-gold-400 text-xs font-bold uppercase opacity-0 group-hover:opacity-100 transition-opacity">Download</span>
              </a>
            ))}
          </div>
        )}
        <button
          onClick={onBack}
          className="mt-4 text-teal-300 hover:text-gold-400 underline transition-colors"
        >
          Back to Store
        </button>
      </div>
    );
  }

  if (status === 'failed' || status === 'expired') {
    return (
      <div className="text-center py-16 space-y-4">
        <AlertCircle size={64} className="text-red-400 mx-auto" />
        <h2 className="text-2xl font-bold text-teal-50">Payment {status === 'expired' ? 'Expired' : 'Failed'}</h2>
        <p className="text-teal-300">Something went wrong. Please try again.</p>
        <button
          onClick={onBack}
          className="mt-6 bg-gold-500 hover:bg-gold-600 text-black font-bold px-8 py-3 rounded-full transition-all duration-200"
        >
          Try Again
        </button>
      </div>
    );
  }

  return null;
};

export const StorePage = () => {
  const [products, setProducts] = useState([]);
  const [loadingProduct, setLoadingProduct] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [downloadFiles, setDownloadFiles] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState('wav');
  const [paidSessionId, setPaidSessionId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/store/products`).then(res => setProducts(res.data.products)).catch(() => {});
  }, []);

  const fetchDownloads = useCallback(async (sessionId, fmt = 'wav') => {
    try {
      const res = await axios.get(`${API}/store/download/${sessionId}?fmt=${fmt}`);
      if (res.data.files) setDownloadFiles(res.data.files);
    } catch (err) {
      console.error('Downloads not yet available:', err.message);
    }
  }, []);

  const handleFormatChange = useCallback(async (fmt) => {
    setSelectedFormat(fmt);
    if (paidSessionId) {
      await fetchDownloads(paidSessionId, fmt);
    }
  }, [paidSessionId, fetchDownloads]);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus('failed');
      return;
    }
    try {
      const res = await axios.get(`${API}/store/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        setPaidSessionId(sessionId);
        await fetchDownloads(sessionId, 'wav');
      } else if (res.data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
      }
    } catch {
      setPaymentStatus('failed');
    }
  }, [fetchDownloads]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      setPaymentStatus('polling');
      pollStatus(sessionId);
      window.history.replaceState({}, '', '/store');
    }
  }, [pollStatus]);

  const handleBuy = async (productId) => {
    setLoadingProduct(productId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/store/checkout`, {
        product_id: productId,
        origin_url: origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setLoadingProduct(null);
    }
  };

  const clearStatus = () => {
    setPaymentStatus(null);
    setDownloadFiles([]);
    setSelectedFormat('wav');
    setPaidSessionId(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-teal-950 to-gray-900 pt-24">
      <div className="container mx-auto px-4 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-teal-50 mb-4">
              Store
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-gold-500 mx-auto mb-6"></div>
            <p className="text-lg text-teal-200 max-w-xl mx-auto">
              Support the music. Own the sound.
            </p>
          </div>

          {paymentStatus ? (
            <PaymentStatus status={paymentStatus} onBack={clearStatus} downloadFiles={downloadFiles} onFormatChange={handleFormatChange} selectedFormat={selectedFormat} />
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onBuy={handleBuy}
                  loading={loadingProduct === product.id}
                />
              ))}
            </div>
          )}

          {/* Info Section */}
          <div className="mt-16 text-center space-y-3">
            <div className="flex items-center justify-center gap-2 text-teal-300">
              <Music size={16} />
              <span className="text-sm">Secure digital downloads via Stripe</span>
            </div>
            <p className="text-teal-400/60 text-xs">
              All purchases are digital downloads. Download links will be sent to your email after payment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
